import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiDownload,
  FiExternalLink,
  FiPrinter,
  FiRefreshCw,
  FiCheckCircle,
  FiCalendar,
  FiAlertCircle,
} from "react-icons/fi";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getDealerAiReportPdf, clearDealerReportsCache } from "../../api/api";

interface AiReportModalProps {
  open: boolean;
  onClose: () => void;
  initialYear?: number;
  dealerDisplayName?: string;
}

const REPORT_YEARS = [2026, 2025, 2024, 2023];

const GENERATION_STEPS = [
  "Connecting to Deco AI Copilot engine...",
  "Analyzing annual order velocity, tier milestones & growth pacing...",
  "Synthesizing competitive benchmarks and predictive forecasts...",
  "Compiling executive high-resolution PDF report...",
];

export default function AiReportModal({
  open,
  onClose,
  initialYear = new Date().getFullYear(),
  dealerDisplayName,
}: AiReportModalProps) {
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState<number>(0);
  const blobRef = useRef<string | null>(null);
  const wasOpenRef = useRef<boolean>(false);

  // Sync initialYear if it changes
  useEffect(() => {
    if (initialYear) {
      setSelectedYear(initialYear);
    }
  }, [initialYear]);

  // Clean up object URLs to prevent memory leaks
  const cleanUpBlob = () => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  };

  const handleClose = () => {
    // Clear server-side report cache when closing modal/PDF
    clearDealerReportsCache().catch((err) => {
      console.warn("Failed to clear dealer reports cache on close:", err);
    });
    onClose();
  };

  useEffect(() => {
    return () => {
      if (wasOpenRef.current) {
        clearDealerReportsCache().catch(() => {});
      }
      cleanUpBlob();
    };
  }, []);

  // Fetch or generate report whenever modal opens or year changes
  const fetchReport = async (year: number) => {
    setLoading(true);
    setError(null);
    setStepIndex(0);

    // Progressive step animation
    const t1 = setTimeout(() => setStepIndex(1), 600);
    const t2 = setTimeout(() => setStepIndex(2), 1200);
    const t3 = setTimeout(() => setStepIndex(3), 1800);

    try {
      const response = await getDealerAiReportPdf(year);
      cleanUpBlob();

      const rawData = response.data;
      const rawBlob = rawData instanceof Blob ? rawData : new Blob([rawData]);

      // Read text content to verify whether it's valid PDF binary or a JSON/HTML error
      const textSample = await rawBlob.slice(0, 2000).text();
      const trimmed = textSample.trim();

      // Check if backend returned JSON (e.g. { success: false, message: ... } or { data: { url: ... } })
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          const fullText = await rawBlob.text();
          const json = JSON.parse(fullText);

          // Check if response contains a direct download/preview URL
          const directUrl =
            json.url ||
            json.pdfUrl ||
            json.data?.url ||
            json.data?.pdfUrl ||
            json.data?.fileUrl;
          if (directUrl) {
            setPdfBlobUrl(directUrl);
            return;
          }

          // Check if response contains base64 encoded PDF
          const base64Data =
            json.base64 ||
            json.data?.base64 ||
            json.pdfBase64 ||
            json.data?.pdfBase64;
          if (base64Data) {
            const cleanBase64 = base64Data.replace(
              /^data:application\/pdf;base64,/,
              ""
            );
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const b64Blob = new Blob([byteNumbers], {
              type: "application/pdf",
            });
            const url = URL.createObjectURL(b64Blob);
            blobRef.current = url;
            setPdfBlobUrl(url);
            return;
          }

          // If JSON contains error or negative success
          const errMsg =
            json.message ||
            json.error ||
            (json.success === false
              ? `Report for ${year} is not yet available.`
              : null);
          if (errMsg) {
            throw new Error(errMsg);
          }
        } catch (jsonErr: any) {
          throw new Error(
            jsonErr.message || `No PDF document found for year ${year}.`
          );
        }
      }

      // Check if response is HTML error page (e.g. 404/500 proxy error)
      if (
        trimmed.toLowerCase().includes("<html") ||
        trimmed.toLowerCase().includes("<!doctype")
      ) {
        throw new Error(
          `Report service currently unavailable for ${year}. Please retry shortly.`
        );
      }

      // Normal valid PDF blob
      const validBlob = new Blob([rawData], { type: "application/pdf" });
      const url = URL.createObjectURL(validBlob);
      blobRef.current = url;
      setPdfBlobUrl(url);
    } catch (err: any) {
      console.error("Failed to generate AI report PDF:", err);
      let errMsg = "Failed to generate AI Executive Report. Please try again.";

      if (err.response?.data instanceof Blob) {
        try {
          const errText = await err.response.data.text();
          const errJson = JSON.parse(errText);
          if (errJson.message) errMsg = errJson.message;
        } catch {}
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      } else if (err.message) {
        errMsg = err.message;
      }

      if (err.response?.status === 404) {
        errMsg = `No report data is currently available for year ${year}.`;
      }

      setError(errMsg);
      toast.error(errMsg);
    } finally {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      fetchReport(selectedYear);
    } else {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        clearDealerReportsCache().catch(() => {});
      }
      cleanUpBlob();
      setPdfBlobUrl(null);
      setError(null);
    }
  }, [open, selectedYear]);

  if (!open) return null;

  const handleDownload = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = `DecoTV_Executive_AI_Report_${selectedYear}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Executive Report for ${selectedYear} downloaded.`);
  };

  const handleOpenNewTab = () => {
    if (!pdfBlobUrl) return;
    window.open(pdfBlobUrl, "_blank");
  };

  const handlePrint = () => {
    if (!pdfBlobUrl) return;
    const printWindow = window.open(pdfBlobUrl, "_blank");
    if (printWindow) {
      printWindow.focus();
      printWindow.print();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 overflow-hidden"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-5xl bg-[#0F1B2E] rounded-2xl shadow-2xl border border-white/10 flex flex-col max-h-[92vh] z-10 font-poppins overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Top Header */}
        <div className="px-5 sm:px-7 py-4 border-b border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#0a1220]/80">
          <div className="flex items-center gap-3.5">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#B08D55]/30 to-[#B08D55]/10 border border-[#B08D55]/40 text-[#B08D55] shadow-lg shadow-[#B08D55]/10 shrink-0">
              <Sparkles className="w-5 h-5 text-[#e6c188]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white tracking-tight">
                  Executive AI Performance Report
                </h3>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-[#B08D55]/20 text-[#e6c188] border border-[#B08D55]/30">
                  AI PDF
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {dealerDisplayName ? (
                  <>
                    Prepared for{" "}
                    <span className="text-[#e6c188] font-medium">
                      {dealerDisplayName}
                    </span>{" "}
                    •{" "}
                  </>
                ) : null}
                Comprehensive annual intelligence, velocity & forecasting
              </p>
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="flex items-center flex-wrap gap-2 justify-end">
            {/* Year Selector */}
            <div className="flex items-center bg-slate-800/80 rounded-xl p-1 border border-white/10 text-xs">
              <FiCalendar className="text-slate-400 ml-2 mr-1.5 hidden sm:inline" />
              {REPORT_YEARS.map((yr) => (
                <button
                  key={yr}
                  onClick={() => setSelectedYear(yr)}
                  disabled={loading}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all cursor-pointer ${
                    selectedYear === yr
                      ? "bg-[#B08D55] text-white shadow-sm font-semibold"
                      : "text-slate-300 hover:text-white hover:bg-white/5"
                  } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {yr}
                </button>
              ))}
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              disabled={loading || !pdfBlobUrl}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#B08D55] hover:bg-[#c29d63] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold shadow-md transition cursor-pointer"
              title="Download PDF"
            >
              <FiDownload size={14} />
              <span className="hidden sm:inline">Download</span>
            </button>

            {/* Print / External */}
            {pdfBlobUrl && !loading && (
              <>
                <button
                  onClick={handlePrint}
                  className="p-2 rounded-xl text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/60 border border-white/10 transition cursor-pointer"
                  title="Print Report"
                >
                  <FiPrinter size={15} />
                </button>
                <button
                  onClick={handleOpenNewTab}
                  className="p-2 rounded-xl text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/60 border border-white/10 transition cursor-pointer"
                  title="Open in new window"
                >
                  <FiExternalLink size={15} />
                </button>
              </>
            )}

            {/* Regenerate */}
            <button
              onClick={() => fetchReport(selectedYear)}
              disabled={loading}
              className="p-2 rounded-xl text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/60 border border-white/10 transition cursor-pointer"
              title="Regenerate Report"
            >
              <FiRefreshCw
                size={15}
                className={loading ? "animate-spin text-[#B08D55]" : ""}
              />
            </button>

            {/* Close */}
            <button
              onClick={handleClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition cursor-pointer ml-1"
              aria-label="Close modal"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Golden separator accent */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#B08D55] to-transparent opacity-60" />

        {/* Modal Content */}
        <div className="relative flex-1 min-h-[520px] max-h-[76vh] flex flex-col bg-slate-950/60 overflow-hidden">
          {loading ? (
            /* EXACT AI Analyzing Animation from Profile Page (AiOverviewModal) */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center relative overflow-hidden">
              {/* CSS Animation Keyframes */}
              <style>{`
                @keyframes geminiFluidBlob1 {
                  0% {
                    border-radius: 42% 58% 70% 30% / 45% 45% 55% 55%;
                    transform: scale(1) rotate(0deg);
                  }
                  33% {
                    border-radius: 70% 30% 46% 54% / 30% 60% 40% 70%;
                    transform: scale(1.15) rotate(120deg);
                  }
                  66% {
                    border-radius: 38% 62% 60% 40% / 50% 30% 70% 50%;
                    transform: scale(0.92) rotate(240deg);
                  }
                  100% {
                    border-radius: 42% 58% 70% 30% / 45% 45% 55% 55%;
                    transform: scale(1) rotate(360deg);
                  }
                }
                @keyframes geminiPulseGlow {
                  0%, 100% {
                    opacity: 0.55;
                    filter: blur(28px);
                    transform: scale(0.92);
                  }
                  50% {
                    opacity: 0.95;
                    filter: blur(38px);
                    transform: scale(1.25);
                  }
                }
              `}</style>

              {/* Platform Brown & Dark Off-White Fluid Morphing Orb */}
              <div className="relative flex items-center justify-center w-32 h-32 mb-6">
                <div
                  className="absolute inset-0 bg-gradient-to-tr from-[#6B4E23]/40 via-[#B08D55]/30 to-[#DFD4C0]/35 rounded-full"
                  style={{ animation: "geminiPulseGlow 3.5s ease-in-out infinite" }}
                />
                <div
                  className="w-28 h-28 opacity-95"
                  style={{
                    background: "linear-gradient(135deg, #5C4018 0%, #8C6836 30%, #CBB799 65%, #EDE5D8 100%)",
                    animation: "geminiFluidBlob1 5.5s ease-in-out infinite",
                    boxShadow: "0 12px 28px -6px rgba(92, 64, 24, 0.35), inset 0 2px 6px rgba(255, 255, 255, 0.4)",
                  }}
                />
              </div>

              {/* Status Pill & Subtitle (Profile Style) */}
              <div className="space-y-2 max-w-sm relative z-10 mb-6">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-gradient-to-r from-amber-500/15 via-purple-500/15 to-blue-500/15 border border-amber-400/30 text-white text-xs font-bold tracking-wide shadow-2xs">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
                  <span>Deco AI Copilot</span>
                </div>
                <h4 className="text-sm font-bold text-white tracking-tight transition-all duration-300">
                  {GENERATION_STEPS[stepIndex]}
                </h4>
              </div>

              {/* Progress Steps Checklist */}
              <div className="w-full max-w-md space-y-2 bg-slate-900/80 p-4 rounded-xl border border-white/10 text-left">
                {GENERATION_STEPS.map((step, idx) => {
                  const isDone = idx < stepIndex;
                  const isCurrent = idx === stepIndex;
                  return (
                    <div
                      key={step}
                      className={`flex items-center gap-3 text-xs transition-colors duration-300 ${
                        isDone
                          ? "text-emerald-400 font-medium"
                          : isCurrent
                          ? "text-[#e6c188] font-semibold"
                          : "text-slate-500"
                      }`}
                    >
                      {isDone ? (
                        <FiCheckCircle
                          className="shrink-0 text-emerald-400"
                          size={15}
                        />
                      ) : isCurrent ? (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-[#e6c188] border-t-transparent animate-spin shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border border-slate-600 shrink-0" />
                      )}
                      <span className="truncate">{step}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : error ? (
            /* Error State */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mb-4">
                <FiAlertCircle size={28} />
              </div>
              <h4 className="text-base font-bold text-white mb-1.5">
                Unable to Load Report for {selectedYear}
              </h4>
              <p className="text-xs text-slate-400 max-w-md mb-5 leading-relaxed">
                {error}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fetchReport(selectedYear)}
                  className="px-4 py-2 rounded-xl bg-[#B08D55] hover:bg-[#c29d63] text-white text-xs font-semibold transition cursor-pointer"
                >
                  Retry Generation
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ) : pdfBlobUrl ? (
            /* Embedded PDF Viewer */
            <div className="flex-1 w-full h-full flex flex-col bg-slate-900">
              <iframe
                src={`${pdfBlobUrl}#toolbar=1&navpanes=0`}
                className="w-full flex-1 border-none bg-slate-900 min-h-[500px]"
                title={`Deco TV Executive AI Report ${selectedYear}`}
              />
            </div>
          ) : null}
        </div>

        {/* Footer info bar */}
        <div className="px-5 py-2.5 bg-[#0a1220] border-t border-white/5 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-400 gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Deco TV AI Copilot Report Engine</span>
            <span className="text-slate-600">•</span>
            <span>Year {selectedYear}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-400">
            <span>High-Resolution Vector PDF</span>
            {pdfBlobUrl && !loading && (
              <button
                onClick={handleDownload}
                className="text-[#e6c188] hover:text-[#d4af37] font-semibold underline underline-offset-2 cursor-pointer"
              >
                Direct Download
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
